import vuetify from '@/plugins/vuetify';

export class MovableUtils {

    private readonly movableElSelector: string;
    private readonly mouseTargetAllowsGrabbing: (event: HTMLElement) => boolean;

    private sharedGrabbingElementConfig: {
        el: HTMLElement;
        mouseStartX: number;
        mouseStartY: number;
        elStartX: number;
        elStartY: number;
        oldTransition: string;
    } | null = null;

    private timerId: number | undefined;

    constructor(movableElSelector: string, grabbedElAllowsMoving: (event: HTMLElement) => boolean) {
        this.movableElSelector = movableElSelector;
        this.mouseTargetAllowsGrabbing = grabbedElAllowsMoving;

        document.addEventListener('mousedown', this.mouseDownListener);
        document.addEventListener('mouseover', this.mouseOverListener);
        document.addEventListener('mousemove', this.mouseMoveListener);
        document.addEventListener('mouseup', this.mouseUpListener);
        this.timerId = setInterval(() => { // prevent out of bounds
            if (this.sharedGrabbingElementConfig) {
                const left = Math.min(parseInt(this.sharedGrabbingElementConfig.el.style.left, 10), window.innerWidth - this.sharedGrabbingElementConfig.el.getBoundingClientRect().width);
                this.sharedGrabbingElementConfig.el.style.left = left + 'px';
                let top = Math.min(parseInt(this.sharedGrabbingElementConfig.el.style.top, 10), window.innerHeight - this.sharedGrabbingElementConfig.el.getBoundingClientRect().height);
                if (top < 0) {
                    top = 0;
                }
                this.sharedGrabbingElementConfig.el.style.top = top + 'px';
            }
        }, 100);
    }

    unregister() {
        document.removeEventListener('mousedown', this.mouseDownListener);
        document.removeEventListener('mouseover', this.mouseOverListener);
        document.removeEventListener('mousemove', this.mouseMoveListener);
        document.removeEventListener('mouseup', this.mouseUpListener);
        clearInterval(this.timerId);
    }

    private readonly mouseDownListener = (e: MouseEvent) => {
        const mouseTarget = e.target;
        if (mouseTarget instanceof HTMLElement) {
            const moveableEl = mouseTarget.closest(this.movableElSelector);
            if (moveableEl instanceof HTMLElement) {
                const userClickedOnButton = e.button !== 0;
                const userClickedOnGrabbableEl = this.mouseTargetAllowsGrabbing(mouseTarget);
                if (moveableEl && !userClickedOnButton && userClickedOnGrabbableEl) { // element which can be used to move element
                    const moveableElBoundingRect = moveableEl.getBoundingClientRect();
                    this.sharedGrabbingElementConfig = {
                        el: moveableEl,
                        mouseStartX: e.clientX,
                        mouseStartY: e.clientY,
                        elStartX: moveableElBoundingRect.left,
                        elStartY: moveableElBoundingRect.top,
                        oldTransition: moveableEl.style.transition
                    };
                    this.sharedGrabbingElementConfig.oldTransition = (moveableEl.style || {}).transition || '';
                    Object.assign(moveableEl.style, {
                        position: 'fixed',
                        margin: '0',
                        transition: 'none'
                    });
                }
            }
        }
    }

    private readonly mouseOverListener = (e: MouseEvent) => {
        const mouseTarget = e.target;
        if (mouseTarget instanceof HTMLElement) {
            const moveableEl = mouseTarget.closest(this.movableElSelector);
            if (moveableEl instanceof HTMLElement) {
                const userClickedOnButton = e.button !== 0;
                const userClickedOnGrabbableEl = this.mouseTargetAllowsGrabbing(mouseTarget);
                if (moveableEl && !userClickedOnButton && userClickedOnGrabbableEl) { // element which can be used to move element
                    mouseTarget.style.cursor = 'move';
                }
            }
        }
    }

    private readonly mouseMoveListener = (e: MouseEvent) => {
        if (!this.sharedGrabbingElementConfig) {
            return;
        }
        const navigationOffsetLeft = vuetify.framework.application.left;
        Object.assign(this.sharedGrabbingElementConfig.el.style, {
            left: Math.min(
                Math.max(navigationOffsetLeft, this.sharedGrabbingElementConfig.elStartX + e.clientX - this.sharedGrabbingElementConfig.mouseStartX, 0),
                window.innerWidth - this.sharedGrabbingElementConfig.el.getBoundingClientRect().width
            ) + 'px',
            top: Math.min(
                Math.max(this.sharedGrabbingElementConfig.elStartY + e.clientY - this.sharedGrabbingElementConfig.mouseStartY, 0),
                window.innerHeight - this.sharedGrabbingElementConfig.el.getBoundingClientRect().height
            ) + 'px'
        });

    }

    private readonly mouseUpListener = () => {
        if (this.sharedGrabbingElementConfig && this.sharedGrabbingElementConfig.el) {
            this.sharedGrabbingElementConfig.el.style.transition = this.sharedGrabbingElementConfig.oldTransition;
        }
        this.sharedGrabbingElementConfig = null;
    }

}
